import { Plus, Trash2 } from "lucide-react";
import type { CompanyLocation, OfficeStaffed } from "../api/CrmApi";
import CountrySelect from "./CountrySelect";
import Button from "shared/Button";
import { FORM_CONTROL } from "shared/tableStyles";

interface Props {
  value: CompanyLocation[];
  onChange: (locations: CompanyLocation[]) => void;
  disabled?: boolean;
}

const CELL = `w-full px-2 py-1.5 text-sm ${FORM_CONTROL}`;

export function blankLocation(): CompanyLocation {
  return { address: null, city: null, state: null, zip: null, country: null, is_hq: false, source: "research", staffed: "unknown" };
}

const STAFFED_OPTIONS: { value: OfficeStaffed; label: string }[] = [
  { value: "unknown", label: "Staffed: unknown" },
  { value: "staffed_office", label: "Staffed office" },
  { value: "mailing_address_only", label: "Mailing address only" },
];

/** What the server could do with the saved address: where the map pin landed, and how precisely. */
export function locatedHint(loc: CompanyLocation): { text: string; located: boolean } | null {
  if (loc.geo_status === "located") {
    if (loc.geo_precision === "city_inferred") {
      return { text: `state guessed${loc.geo_state ? ` (${loc.geo_state})` : ""} — add the state`, located: false };
    }
    return { text: loc.geo_precision === "zip" ? "located by zip" : "located by city only", located: true };
  }
  if (loc.geo_status === "not_located") return { text: "not located", located: false };
  return null; // not applicable (country-only / foreign), or not saved yet — nothing to say
}

// Editing any of these makes the saved coordinates and hint stale until the next save.
const ADDRESS_FIELDS = ["address", "city", "state", "zip", "country"] as const;

/** The company's offices: one row per office (address, city, state, zip, country, HQ). Offices added here are
 * "research" entries; ones auto-added from the primes CSV keep their "sheet" source and can be corrected or removed.
 * A company counts as US-confirmed when ANY office is in the United States. */
export default function LocationsEditor({ value, onChange, disabled }: Props) {
  const update = (index: number, patch: Partial<CompanyLocation>) =>
    onChange(
      value.map((loc, i) => {
        if (i !== index) return loc;
        const stale = ADDRESS_FIELDS.some((f) => f in patch)
          ? { lat: undefined, lng: undefined, geo_precision: undefined, geo_status: undefined }
          : {};
        return { ...loc, ...patch, ...stale };
      }),
    );
  const text = (loc: CompanyLocation, field: "address" | "city" | "state" | "zip") => loc[field] ?? "";
  const asNull = (s: string) => (s.trim() === "" ? null : s);

  return (
    <div className="space-y-2">
      {value.length === 0 && <p className="text-xs text-muted-foreground">No offices on file.</p>}
      {value.map((loc, i) => (
        <div key={i} className="rounded-lg border border-border p-2 space-y-1.5" data-testid="location-row">
          <div className="grid grid-cols-2 gap-1.5">
            <input
              aria-label={`Office ${i + 1} address`} placeholder="Street address" disabled={disabled}
              value={text(loc, "address")} onChange={(e) => update(i, { address: asNull(e.target.value) })}
              className={`${CELL} col-span-2`}
            />
            <input
              aria-label={`Office ${i + 1} city`} placeholder="City" disabled={disabled}
              value={text(loc, "city")} onChange={(e) => update(i, { city: asNull(e.target.value) })} className={CELL}
            />
            <input
              aria-label={`Office ${i + 1} state`} placeholder="State" disabled={disabled}
              value={text(loc, "state")} onChange={(e) => update(i, { state: asNull(e.target.value) })} className={CELL}
            />
            <input
              aria-label={`Office ${i + 1} zip`} placeholder="Zip" disabled={disabled}
              value={text(loc, "zip")} onChange={(e) => update(i, { zip: asNull(e.target.value) })} className={CELL}
            />
            <CountrySelect
              value={loc.country ?? ""} onChange={(c) => update(i, { country: c || null })} disabled={disabled}
              className={CELL}
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <select
              aria-label={`Office ${i + 1} staffed`} disabled={disabled}
              value={loc.staffed ?? "unknown"} onChange={(e) => update(i, { staffed: e.target.value as OfficeStaffed })}
              className={`px-2 py-1 text-xs ${FORM_CONTROL}`}
              title="A mailing address or virtual office does not count as local presence"
            >
              {STAFFED_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            {locatedHint(loc) && (
              <span className={`text-[11px] ${locatedHint(loc)!.located ? "text-muted-foreground" : "text-warning-soft-foreground"}`}>
                {locatedHint(loc)!.text}
              </span>
            )}
          </div>
          <div className="flex items-center justify-between">
            <label className="flex items-center gap-1.5 text-xs text-foreground cursor-pointer">
              <input
                type="checkbox" checked={loc.is_hq} disabled={disabled}
                onChange={(e) => update(i, { is_hq: e.target.checked })} className="accent-primary"
              />
              Headquarters
              <span className="text-muted-foreground">· {loc.source}</span>
            </label>
            <button
              type="button" aria-label={`Remove office ${i + 1}`} disabled={disabled}
              onClick={() => onChange(value.filter((_, j) => j !== i))}
              className="text-muted-foreground hover:text-destructive"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      ))}
      <Button variant="outline" size="sm" type="button" disabled={disabled} onClick={() => onChange([...value, blankLocation()])}>
        <Plus className="w-3.5 h-3.5" /> Add office
      </Button>
    </div>
  );
}
